import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  useConfirm,
  useToast,
} from "../../../../design-system";
import {
  createDocumentationLevel,
  deleteDocumentationLevel,
  updateDocumentationLevel,
} from "../../../../helpers/internal_documentation_helper";
import { TEXTAREA_STYLE } from "../../constants";

export function LevelsCard({ levels, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null); // null | {} | level
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ position: "", code: "", name: "", description: "" });

  const openCreate = () => {
    setEditing({});
    setForm({
      position: String((levels?.[levels.length - 1]?.position || 0) + 1),
      code: "",
      name: "",
      description: "",
    });
    setError(null);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      position: String(l.position),
      code: l.code,
      name: l.name,
      description: l.description || "",
    });
    setError(null);
  };

  const close = () => {
    if (submitting) return;
    setEditing(null);
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const position = Number(form.position);
    if (!Number.isInteger(position) || position < 1) {
      return setError("La posición debe ser un número entero positivo.");
    }
    if (!form.code.trim()) return setError("El código es obligatorio.");
    if (!form.name.trim()) return setError("El nombre es obligatorio.");

    const payload = {
      position,
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
    };

    setSubmitting(true);
    try {
      if (editing?.id) {
        await updateDocumentationLevel(editing.id, payload);
        toast.success("Nivel actualizado");
      } else {
        await createDocumentationLevel(payload);
        toast.success("Nivel creado");
      }
      setEditing(null);
      onChanged?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo guardar el nivel.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (l) => {
    const ok = await confirm({
      title: "Eliminar nivel",
      description: `Eliminar el nivel "${l.name}". Solo es posible si no hay documentos ni tipos que lo referencien.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteDocumentationLevel(l.id);
      toast.success("Nivel eliminado");
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  const formId = "doc-level-form";

  return (
    <Card padded={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "16px 20px",
          borderBottom: "1px solid var(--kr-border)",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
            Niveles jerárquicos
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Pirámide documental del workspace (1 = nivel más alto). Cada nivel puede contener cualquier tipo de documento.
          </div>
        </div>
        <Button variant="primary" icon="Plus" onClick={openCreate}>
          Nuevo nivel
        </Button>
      </div>
      {(levels || []).length === 0 ? (
        <EmptyState
          icon="Layers"
          title="Sin niveles"
          description="Crea el primer nivel (ej. Política, Procedimiento)."
        />
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "80px 100px 1fr 1fr 100px",
              gap: 12,
              padding: "10px 20px",
              background: "var(--kr-neutral-50)",
              borderBottom: "1px solid var(--kr-border)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
              color: "var(--kr-fg-muted)",
            }}
          >
            <span>Posición</span>
            <span>Código</span>
            <span>Nombre</span>
            <span>Descripción</span>
            <span style={{ textAlign: "right" }}>Acciones</span>
          </div>
          {levels.map((l, i) => (
            <div
              key={l.id}
              style={{
                display: "grid",
                gridTemplateColumns: "80px 100px 1fr 1fr 100px",
                gap: 12,
                alignItems: "center",
                padding: "10px 20px",
                borderBottom: i === levels.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                fontSize: 13,
              }}
            >
              <Badge tone="neutral">#{l.position}</Badge>
              <span style={{ fontFamily: "var(--kr-font-mono, ui-monospace, monospace)", fontSize: 12 }}>
                {l.code}
              </span>
              <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{l.name}</span>
              <span style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>{l.description || "—"}</span>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
                <IconBtn icon="PenLine" title="Editar" onClick={() => openEdit(l)} />
                <IconBtn icon="Trash2" title="Eliminar" onClick={() => onDelete(l)} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={editing !== null}
        onClose={close}
        size="sm"
        title={editing?.id ? "Editar nivel" : "Nuevo nivel"}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
              Cancelar
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="primary" type="submit" form={formId} disabled={submitting}>
              {submitting ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {error && <Alert tone="danger">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
            <Field label="Posición" required help="1 = nivel más alto">
              <Input
                type="number"
                min={1}
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                required
              />
            </Field>
            <Field label="Código" required>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="Ej. POL"
                required
              />
            </Field>
          </div>
          <Field label="Nombre" required>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej. Política"
              required
            />
          </Field>
          <Field label="Descripción">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              style={TEXTAREA_STYLE}
            />
          </Field>
        </form>
      </Drawer>
    </Card>
  );
}
